import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import useHasSmartOnboardingAccess from '@/composables/useHasSmartOnboardingAccess';
import {
  COMPLETE,
  LOCKED,
  NEEDS_PREVIOUS,
  NOT_SYNCED,
  PENDING,
  PROGRESS,
  StatusType,
  StepType,
} from '@/constants/onboarding';
import { captureException } from '@/errors';
import { ls } from '@/local_storage';
import { useDataSharesStore, usePartnersStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import { isManagedOfflinePartner, isOfflinePartner } from '@/types/partners';
import { OnboardingConfirmationType } from '@/types/root';

import useNewOnboarding from './useNewOnboarding';

type Step = {
  status: StatusType;
  isActive: boolean;
};

type ConnectDataPhaseType = 'connect' | 'setup' | 'syncing';

const activeStep = ref<null | number>(null);
const isSyncTriggered = ref(false);
const isBootstrapping = ref(false);
const isSegmentDataConfirmed = ref(false);
const isShareDataConfirmed = ref(false);
const acceptedInviteSender = ref('');
const hasSeenAcceptedInvitePartner = ref(false);
const isPublicInviteSent = ref(false);
const publicInviteSentOrgName = ref('');
const publicInviteSentProposalId = ref<number | null>(null);

export default function () {
  const rootStore = useRootStore();
  const currentOrgId = computed(() => rootStore.currentOrg?.id);

  watch(
    currentOrgId,
    (orgId) => {
      if (orgId) {
        isSegmentDataConfirmed.value =
          !!rootStore.currentOrg?.segment_data_confirmed ||
          ls.segmentDataConfirmed.get() === orgId;
        isShareDataConfirmed.value =
          !!rootStore.currentOrg?.share_data_confirmed ||
          ls.shareDataConfirmed.get() === orgId;
        const stored = ls.acceptedInviteSender.get();
        if (stored?.orgId === orgId) {
          acceptedInviteSender.value = stored.name || 'Partner';
          hasSeenAcceptedInvitePartner.value = !!stored.hasSeenPartner;
        } else {
          acceptedInviteSender.value = '';
          hasSeenAcceptedInvitePartner.value = false;
        }
        const storedPublicInvite = ls.publicInviteSentOrg.get();
        if (storedPublicInvite?.orgId === orgId) {
          isPublicInviteSent.value = true;
          publicInviteSentOrgName.value = storedPublicInvite.name || 'Partner';
          publicInviteSentProposalId.value =
            storedPublicInvite.proposalId ?? null;
        } else {
          isPublicInviteSent.value = false;
          publicInviteSentOrgName.value = '';
          publicInviteSentProposalId.value = null;
        }
      } else {
        isSegmentDataConfirmed.value = false;
        isShareDataConfirmed.value = false;
        acceptedInviteSender.value = '';
        hasSeenAcceptedInvitePartner.value = false;
        isPublicInviteSent.value = false;
        publicInviteSentOrgName.value = '';
        publicInviteSentProposalId.value = null;
      }
    },
    { immediate: true },
  );

  const isSmartOnboarding = useHasSmartOnboardingAccess();

  const currentUser = computed(() => rootStore.currentUser);

  const isInitialPhase = computed(
    () => isSmartOnboarding.value && !currentUser.value?.department,
  );

  const {
    isLoading,
    importDataStepStatus,
    invitePartnerStepStatus,
    hasCustomersPopulation,
    request,
    partner,
    isUserInvited,
    numStepsLeft: v1NumStepsLeft,
  } = useNewOnboarding();

  const { isSharingDataWithPartners } = storeToRefs(useDataSharesStore());
  const partnersStore = usePartnersStore();
  const { proposalsSent, partnerOrgs, publicInviteOrg, publicInviteCode } =
    storeToRefs(partnersStore);

  watch(
    () => partnersStore.ready,
    (isReady) => {
      if (isReady) partnersStore.hydratePublicInviteFromLocalStorage();
    },
    { immediate: true },
  );

  const hasRealPartner = computed(() =>
    partnerOrgs.value.some(
      (org) => !isOfflinePartner(org) && !isManagedOfflinePartner(org),
    ),
  );

  /* ── Step 1: Connect Data ── */
  const connectDataStatus = computed<StatusType>(
    () => importDataStepStatus.value,
  );

  const connectDataPhase = computed<ConnectDataPhaseType>(() => {
    if (isSyncTriggered.value && connectDataStatus.value !== COMPLETE)
      return 'syncing';
    if (connectDataStatus.value === NOT_SYNCED) return 'setup';
    if (connectDataStatus.value === PENDING) return 'syncing';
    return 'connect';
  });

  /* ── Step 0 (conditional): Send Public Invite ── */
  const isPublicInvitePending = computed(
    () =>
      !!publicInviteOrg.value &&
      !!publicInviteCode.value &&
      publicInviteCode.value !== rootStore.currentOrg?.public_invite_code &&
      !isPublicInviteSent.value &&
      !acceptedInviteSender.value,
  );

  const hasPendingProposal = computed(
    () =>
      !!request.value && !acceptedInviteSender.value && !hasRealPartner.value,
  );

  const hasInvitedStep = computed(
    () =>
      (hasPendingProposal.value || !!acceptedInviteSender.value) &&
      !isPublicInvitePending.value,
  );

  const hasPublicInviteStep = computed(
    () =>
      isPublicInvitePending.value ||
      (isPublicInviteSent.value && !hasInvitedStep.value),
  );

  const sendPublicInviteStep = computed<Step>(() => ({
    isActive:
      activeStep.value === StepType.SendPublicInvite &&
      isPublicInvitePending.value,
    status: isPublicInvitePending.value ? PROGRESS : COMPLETE,
  }));

  function confirmPublicInviteSent(orgName: string, proposalId: number) {
    const senderName = orgName || 'Partner';
    publicInviteSentOrgName.value = senderName;
    publicInviteSentProposalId.value = proposalId;
    isPublicInviteSent.value = true;
    partnersStore.setPublicInviteOrg(null);
    partnersStore.setPublicInviteCode('');
    ls.publicInvite.set();
    if (currentOrgId.value) {
      ls.publicInviteSentOrg.set({
        orgId: currentOrgId.value,
        name: senderName,
        proposalId,
      });
    }
  }

  function cancelPublicInvite() {
    isPublicInviteSent.value = false;
    publicInviteSentOrgName.value = '';
    publicInviteSentProposalId.value = null;
    partnersStore.setPublicInviteOrg(null);
    partnersStore.setPublicInviteCode('');
    ls.publicInvite.set();
    const storedPublicInviteSent = ls.publicInviteSentOrg.get();
    if (
      !storedPublicInviteSent ||
      storedPublicInviteSent.orgId === currentOrgId.value
    ) {
      ls.publicInviteSentOrg.set(null);
    }
  }

  const acceptInviteStep = computed<Step>(() => ({
    isActive:
      activeStep.value === StepType.AcceptInvite && hasPendingProposal.value,
    status: hasPendingProposal.value ? PROGRESS : COMPLETE,
  }));

  /* ── Step 1: Connect Data ── */
  const connectDataStep = computed<Step>(() => ({
    isActive: activeStep.value === StepType.ConnectData,
    status:
      connectDataPhase.value === 'syncing'
        ? COMPLETE
        : isPublicInvitePending.value || hasPendingProposal.value
          ? NEEDS_PREVIOUS
          : connectDataStatus.value,
  }));

  /* ── Step 2: Segment Data ── */
  const segmentDataStatus = computed<StatusType>(() => {
    if (
      connectDataStatus.value === COMPLETE &&
      hasCustomersPopulation.value &&
      isSegmentDataConfirmed.value
    )
      return COMPLETE;
    if (connectDataPhase.value === 'syncing' || isBootstrapping.value)
      return PENDING;
    if (connectDataStatus.value === COMPLETE) return PROGRESS;
    return NEEDS_PREVIOUS;
  });

  const segmentDataStep = computed<Step>(() => ({
    isActive: activeStep.value === StepType.SegmentData,
    status: segmentDataStatus.value,
  }));

  /* ── Step 3: Define Sharing Settings ── */
  const isShareDataComplete = computed(
    () => isShareDataConfirmed.value || isSharingDataWithPartners.value,
  );

  const shareDataStep = computed<Step>(() => ({
    isActive: activeStep.value === StepType.ShareData,
    status:
      segmentDataStatus.value !== COMPLETE
        ? NEEDS_PREVIOUS
        : isShareDataComplete.value
          ? COMPLETE
          : PROGRESS,
  }));

  /* ── Step 4: Invite Partner (blocked until sharing is done) ── */
  const isInviteStepOptional = computed(
    () =>
      hasRealPartner.value ||
      !!acceptedInviteSender.value ||
      isPublicInviteSent.value ||
      !!proposalsSent.value.length,
  );

  const invitePartnerStep = computed<Step>(() => ({
    isActive: activeStep.value === StepType.InvitePartner,
    status:
      shareDataStep.value.status === COMPLETE
        ? invitePartnerStepStatus.value === LOCKED
          ? LOCKED
          : PROGRESS
        : NEEDS_PREVIOUS,
  }));

  /* ── Active step ── */
  const defaultActiveStep = computed(() => {
    if (isPublicInvitePending.value) return StepType.SendPublicInvite;
    if (hasPendingProposal.value && hasInvitedStep.value)
      return StepType.AcceptInvite;
    if (connectDataPhase.value === 'syncing' || isBootstrapping.value)
      return StepType.SegmentData;
    if (connectDataStatus.value !== COMPLETE) return StepType.ConnectData;
    if (segmentDataStatus.value !== COMPLETE) return StepType.SegmentData;
    if (!isShareDataComplete.value) return StepType.ShareData;
    return StepType.InvitePartner;
  });

  function handleChangeActiveStep(type: StepType) {
    activeStep.value = type;
  }

  function triggerSync() {
    isSyncTriggered.value = true;
  }

  async function patchConfirmation(payload: OnboardingConfirmationType) {
    const { error } = await crossbeamApi.PATCH('/v0.1/organization', {
      body: payload,
    });
    if (error) {
      captureException(error);
      return false;
    }
    return true;
  }

  async function confirmSegmentData() {
    if (!currentOrgId.value) return;
    const wasConfirmed = isSegmentDataConfirmed.value;
    isSegmentDataConfirmed.value = true;
    rootStore.setOnboardingConfirmation({ segment_data_confirmed: true });
    const isConfirmed = await patchConfirmation({
      segment_data_confirmed: true,
    });
    if (!isConfirmed) {
      isSegmentDataConfirmed.value = wasConfirmed;
      rootStore.setOnboardingConfirmation({
        segment_data_confirmed: wasConfirmed,
      });
    }
  }

  function invalidateSegmentData() {
    if (!isSegmentDataConfirmed.value) return;
    isSegmentDataConfirmed.value = false;
    ls.segmentDataConfirmed.set(null);

    const payload: OnboardingConfirmationType = {
      segment_data_confirmed: false,
    };

    if (isShareDataConfirmed.value) {
      isShareDataConfirmed.value = false;
      ls.shareDataConfirmed.set(null);
      payload.share_data_confirmed = false;
    }

    rootStore.setOnboardingConfirmation(payload);
    patchConfirmation(payload);
  }

  async function confirmShareData() {
    if (!currentOrgId.value) return;
    const wasConfirmed = isShareDataConfirmed.value;
    isShareDataConfirmed.value = true;
    rootStore.setOnboardingConfirmation({ share_data_confirmed: true });
    const isConfirmed = await patchConfirmation({
      share_data_confirmed: true,
    });
    if (!isConfirmed) {
      isShareDataConfirmed.value = wasConfirmed;
      rootStore.setOnboardingConfirmation({
        share_data_confirmed: wasConfirmed,
      });
    }
  }

  function invalidateShareData() {
    if (!isShareDataConfirmed.value) return;
    isShareDataConfirmed.value = false;
    ls.shareDataConfirmed.set(null);
    rootStore.setOnboardingConfirmation({ share_data_confirmed: false });
    patchConfirmation({ share_data_confirmed: false });
  }

  function confirmInviteAccepted(name: string) {
    if (!currentOrgId.value) return;
    const senderName = name || 'Partner';
    acceptedInviteSender.value = senderName;
    hasSeenAcceptedInvitePartner.value = false;
    ls.acceptedInviteSender.set({
      orgId: currentOrgId.value,
      name: senderName,
      hasSeenPartner: false,
    });
  }

  function clearAcceptedInvite() {
    acceptedInviteSender.value = '';
    hasSeenAcceptedInvitePartner.value = false;
    ls.acceptedInviteSender.set(null);
  }

  watch(
    [isLoading, defaultActiveStep],
    ([loading, step]: [boolean, StepType]) => {
      if (!loading && activeStep.value !== step) {
        activeStep.value = step;
      }
    },
    { immediate: true },
  );

  watch(hasInvitedStep, (hasStep) => {
    if (!hasStep && activeStep.value === StepType.AcceptInvite) {
      activeStep.value = defaultActiveStep.value;
    }
  });

  watch(hasPublicInviteStep, (hasStep) => {
    if (!hasStep && activeStep.value === StepType.SendPublicInvite) {
      activeStep.value = defaultActiveStep.value;
    }
  });

  watch(
    proposalsSent,
    () => {
      if (
        !isPublicInviteSent.value ||
        !partnersStore.ready ||
        !partnersStore.hasLoadedSentProposals
      ) {
        return;
      }
      const hasMatchingProposal = publicInviteSentProposalId.value
        ? proposalsSent.value.some(
            (p) => p.id === publicInviteSentProposalId.value,
          )
        : proposalsSent.value.some(
            (p) => p.contact_company === publicInviteSentOrgName.value,
          );
      if (!hasMatchingProposal) {
        cancelPublicInvite();
      }
    },
    { immediate: true },
  );

  watch(
    [partnerOrgs, () => partnersStore.ready],
    () => {
      if (!acceptedInviteSender.value || !partnersStore.ready) return;

      if (hasRealPartner.value) {
        if (!hasSeenAcceptedInvitePartner.value && currentOrgId.value) {
          hasSeenAcceptedInvitePartner.value = true;
          ls.acceptedInviteSender.set({
            orgId: currentOrgId.value,
            name: acceptedInviteSender.value,
            hasSeenPartner: true,
          });
        }
        return;
      }

      if (!request.value && hasSeenAcceptedInvitePartner.value) {
        clearAcceptedInvite();
      }
    },
    { immediate: true },
  );

  watch(
    connectDataStatus,
    (status, oldStatus) => {
      if (status !== NOT_SYNCED) isSyncTriggered.value = false;
      if (status !== COMPLETE && oldStatus === COMPLETE)
        invalidateSegmentData();
    },
    { immediate: true },
  );

  watch(isLoading, (loading, wasLoading) => {
    if (wasLoading && !loading && connectDataStatus.value !== COMPLETE)
      invalidateSegmentData();
  });

  /* ── Progress ── */
  const hasExtraStep = computed(
    () => hasInvitedStep.value || hasPublicInviteStep.value,
  );

  const v2NumStepsLeft = computed(() => {
    const steps: StatusType[] = [
      connectDataStep.value.status,
      segmentDataStep.value.status,
      shareDataStep.value.status,
      invitePartnerStep.value.status,
    ];
    if (hasInvitedStep.value) steps.unshift(acceptInviteStep.value.status);
    else if (hasPublicInviteStep.value)
      steps.unshift(sendPublicInviteStep.value.status);
    return steps.filter((step) => step !== COMPLETE).length;
  });

  const totalSteps = computed(() => {
    if (!isSmartOnboarding.value) return 4;
    return hasExtraStep.value ? 5 : 4;
  });

  const numStepsLeft = computed(() =>
    isSmartOnboarding.value ? v2NumStepsLeft.value : v1NumStepsLeft.value,
  );

  return {
    isLoading,
    isSmartOnboarding,
    isInitialPhase,
    isBootstrapping,
    hasPendingProposal,
    hasInvitedStep,
    acceptInviteStep,
    hasPublicInviteStep,
    sendPublicInviteStep,
    isPublicInvitePending,
    publicInviteSentOrgName,
    defaultActiveStep,
    connectDataStatus,
    connectDataPhase,
    connectDataStep,
    segmentDataStep,
    shareDataStep,
    invitePartnerStep,
    isInviteStepOptional,
    request,
    partner,
    isUserInvited,
    acceptedInviteSender,
    numStepsLeft,
    totalSteps,
    handleChangeActiveStep,
    triggerSync,
    confirmSegmentData,
    invalidateSegmentData,
    confirmShareData,
    invalidateShareData,
    confirmInviteAccepted,
    confirmPublicInviteSent,
    cancelPublicInvite,
  };
}

export function __resetSmartOnboarding() {
  activeStep.value = null;
  isSyncTriggered.value = false;
  isBootstrapping.value = false;
  isSegmentDataConfirmed.value = false;
  isShareDataConfirmed.value = false;
  acceptedInviteSender.value = '';
  hasSeenAcceptedInvitePartner.value = false;
  isPublicInviteSent.value = false;
  publicInviteSentOrgName.value = '';
  publicInviteSentProposalId.value = null;
  ls.acceptedInviteSender.set(null);
  ls.publicInviteSentOrg.set(null);
}
